import { useInfiniteQuery } from "@tanstack/react-query";
import { resolvePendingGeneratedTextTitle } from "../utils/resolvePendingGeneratedTextTitle";
import { GeneratedText, GeneratedTextPage } from "./types/GeneratedTexts";

export const REEPRISE_LIST_QUERY_KEY = "reeprise-infinite-list-query";

function isPendingDailyTextMissingDisplayTitle(text: GeneratedText) {
  return (
    text.origin === "DAILY_NUDGE" &&
    text.status === "PENDING" &&
    !resolvePendingGeneratedTextTitle({
      status: text.status,
      topic: text.topic,
      cachedPromptTopic: "",
    })
  );
}

export function shouldPollReepriseList(results: GeneratedText[]) {
  const isGeneratingManualText = results.some(
    (text) => text.status === "PENDING" && text.origin !== "DAILY_NUDGE",
  );

  const isDailyTitleStillPending = results.some(
    isPendingDailyTextMissingDisplayTitle,
  );

  const waitingForPodcastAudio = results.some(
    (text) =>
      text.text_type === "PODCAST" &&
      // keep polling until a speech URL is present
      !text.speech,
  );

  return (
    isGeneratingManualText || isDailyTitleStillPending || waitingForPodcastAudio
  );
}

const fetchGeneratedTextPage = async ({
  pageParam,
  signal,
}: {
  pageParam: string;
  signal: AbortSignal;
}) => {
  const res = await fetch(
    `/api/reeprise/generated_texts/${pageParam ? `?cursor=${pageParam}` : ""}`,
    { signal },
  );
  if (!res.ok) {
    throw new Error("could not fetch reeprises");
  }
  return (await res.json()) as GeneratedTextPage;
};

export function useGeneratedTextPaginatedList() {
  const paginatedGeneratedTextListQuery = useInfiniteQuery({
    queryKey: [REEPRISE_LIST_QUERY_KEY],
    queryFn: fetchGeneratedTextPage,
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next,
    refetchInterval: (query) => {
      const results =
        query.state.data?.pages.flatMap((page) => page.results) ?? [];
      if (shouldPollReepriseList(results)) {
        return 1000;
      }
      return false;
    },
    staleTime: 400,
  });

  return paginatedGeneratedTextListQuery;
}
